import i18n from "i18next";
import { User, UserManager, WebStorageStateStore } from "oidc-client-ts";

const CALLBACK_PATH = "/auth/callback";

type SigninState = { returnTo: string };

/**
 * Keycloak login for the web app. Everyone has a session: visitors without one are logged in as a new guest,
 * and guests can log in or sign up to switch to an account.
 */
export class AuthService {
    private readonly userManager: UserManager;

    constructor(params: { keycloakUrl: string, realm: string, clientId: string }) {
        const origin = window.location.origin;
        this.userManager = new UserManager({
            authority: `${params.keycloakUrl}/realms/${params.realm}`,
            client_id: params.clientId,
            redirect_uri: origin + CALLBACK_PATH,
            post_logout_redirect_uri: origin + "/",
            response_type: "code",
            // offline_access keeps guests' identity after the SSO session ends.
            scope: "openid offline_access",
            userStore: new WebStorageStateStore({ store: window.localStorage }),
            // Renews with the refresh token shortly before the access token expires.
            automaticSilentRenew: true,
            monitorSession: false,
        });
    }

    public isCallback(): boolean {
        return window.location.pathname === CALLBACK_PATH;
    }

    // Completes a login redirect and returns the path the user started from.
    public async handleCallback(): Promise<string> {
        const user = await this.userManager.signinRedirectCallback();
        return (user.state as SigninState | undefined)?.returnTo ?? "/";
    }

    // The stored session, refreshed if its access token has expired. Null if there is no usable session.
    public async getUser(): Promise<User | null> {
        const user = await this.userManager.getUser();
        if (!user || !user.expired) return user;
        try {
            return await this.userManager.signinSilent();
        } catch (error) {
            console.warn("(Auth) Could not refresh the session", error);
            await this.userManager.removeUser();
            return null;
        }
    }

    public async getAccessToken(): Promise<string> {
        const user = await this.getUser();
        if (!user) throw new Error("Not logged in");
        return user.access_token;
    }

    public onUserLoaded(callback: (user: User) => void): () => void {
        this.userManager.events.addUserLoaded(callback);
        return () => this.userManager.events.removeUserLoaded(callback);
    }

    public loginAsGuest(returnTo = currentPath()): Promise<void> {
        return this.userManager.signinRedirect({ extraQueryParams: { guest: "true" }, state: { returnTo } });
    }

    // prompt=login shows the login form even though the guest already has a session.
    public login(returnTo = currentPath()): Promise<void> {
        return this.userManager.signinRedirect({ prompt: "login", ui_locales: i18n.language, state: { returnTo } });
    }

    public signUp(returnTo = currentPath()): Promise<void> {
        return this.userManager.signinRedirect({ prompt: "create", ui_locales: i18n.language, state: { returnTo } });
    }

    public logout(): Promise<void> {
        return this.userManager.signoutRedirect();
    }
}

function currentPath(): string {
    return window.location.pathname + window.location.search;
}
